/**
 * Types voor de HRM-module (skill matrix + certificaten-overzicht).
 *
 * De HRM-module is een dashboard-laag boven bestaande Employee/Skill/
 * Certificate-data. Geen eigen Prisma-modellen — gebruikt:
 *   • `Skill` + `EmployeeSkill` (bestaand)
 *   • `CertificateType` + `EmployeeCertificate` + `CertificateAttachment` (bestaand)
 *
 * Permissie-gate: Skillmatrix v2 hangt onder `SKILLS` module-permissie.
 */

import { z } from 'zod'
import type { CertificateStatus } from './index'

// ─── Skill matrix ────────────────────────────────────────────────────────────

export const SKILL_CATEGORIES = [
  'PROGRAMMERING',
  'MECHANIEK',
  'SOFTWARE',
  'ELEKTRONICA',
  'HYDRAULIEK',
  'PNEUMATIEK',
  'AUTOMATISERING',
  'COMMUNICATIE',
  'PROJECTMANAGEMENT',
  'REGELTECHNIEK',
  'MEETTECHNIEK',
  'VEILIGHEID',
  'ROBOTICA',
  'OVERIG',
] as const

export type SkillCategory = (typeof SKILL_CATEGORIES)[number]

export const SKILL_CATEGORY_LABELS: Record<SkillCategory, string> = {
  PROGRAMMERING: 'Programmering',
  MECHANIEK: 'Mechaniek',
  SOFTWARE: 'Software',
  ELEKTRONICA: 'Elektronica',
  HYDRAULIEK: 'Hydrauliek',
  PNEUMATIEK: 'Pneumatiek',
  AUTOMATISERING: 'Automatisering',
  COMMUNICATIE: 'Communicatie',
  PROJECTMANAGEMENT: 'Projectmanagement',
  REGELTECHNIEK: 'Regeltechniek',
  MEETTECHNIEK: 'Meettechniek',
  VEILIGHEID: 'Veiligheid',
  ROBOTICA: 'Robotica',
  OVERIG: 'Overig',
}

/**
 * Ervaringsniveau is opgeslagen op `Employee.experienceLevel` (JUNIOR /
 * MEDIOR / SENIOR / EXPERT). De skill-matrix toont dit als de "niveau"-
 * indicator omdat per-skill-niveaus niet apart in het datamodel zitten —
 * alleen de assignment-relatie. Dit weerspiegelt de huidige fs-pro-
 * implementatie waarbij een werknemer per skill `geassigneerd` is.
 */
export const EXPERIENCE_LEVELS = ['JUNIOR', 'MEDIOR', 'SENIOR', 'EXPERT'] as const
export type ExperienceLevel = (typeof EXPERIENCE_LEVELS)[number]

export const EXPERIENCE_LEVEL_LABELS: Record<ExperienceLevel, string> = {
  JUNIOR: 'Junior',
  MEDIOR: 'Medior',
  SENIOR: 'Senior',
  EXPERT: 'Expert',
}

export interface SkillDTO {
  id: string
  name: string
  category: SkillCategory
  // Skillmatrix niveauteksten (batch-8) — per-skill schaal. Optioneel: niet elke
  // skill-serializer vult het, de UI valt dan terug op de globale 1..10-schaal.
  schaalMin?: number
  schaalMax?: number
}

/**
 * Per-skill niveau 1-10 (skillmatrix v2).
 *   1  = Geen kennis       6  = Competent
 *   2  = Herkent begrippen  7  = Gevorderd
 *   3  = Basis o.l.v.      8  = Specialist
 *   4  = Onder begeleiding  9  = Expert
 *   5  = Zelfstandig       10 = Autoriteit
 *   `null` = geen niveau ingesteld.
 *
 * Skillmatrix v2 (spec 2026-05-25) — schaal migratie van 1-5 → 1-10.
 * Bestaande 1-5 waarden in de DB blijven geldig binnen 1-10.
 */
export type SkillLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  1:  'Geen kennis',
  2:  'Herkent begrippen',
  3:  'Basis o.l.v.',
  4:  'Onder begeleiding',
  5:  'Zelfstandig',
  6:  'Competent',
  7:  'Gevorderd',
  8:  'Specialist',
  9:  'Expert',
  10: 'Autoriteit',
}

/** Bron-van-waarheid voor effectief niveau: management > eigen > legacy level. */
export function bepaalEffectiefSkillNiveau(
  level: number | null,
  eigenBeoordeling: number | null,
  managementBeoordeling: number | null,
): number | null {
  if (managementBeoordeling !== null && managementBeoordeling !== undefined) {
    return managementBeoordeling
  }
  if (eigenBeoordeling !== null && eigenBeoordeling !== undefined) {
    return eigenBeoordeling
  }
  return level
}

export interface SkillMatrixSkillCell {
  skillId: string
  hasSkill: boolean
  /** Skillmatrix v2 — per-skill niveau 1-10; null = niveau niet ingesteld */
  level: SkillLevel | null
}

export interface SkillMatrixRowDTO {
  employeeId: string
  employeeNumber: string | null
  firstName: string
  lastName: string
  functionTitle: string | null
  experienceLevel: ExperienceLevel | null
  skills: SkillMatrixSkillCell[]
}

export interface SkillMatrixOverviewDTO {
  rows: SkillMatrixRowDTO[]
  skills: SkillDTO[]
}

// ─── Certificate overview ────────────────────────────────────────────────────

export const CERTIFICATE_STATUS_LABELS: Record<CertificateStatus, string> = {
  ACTIEF: 'Geldig',
  BIJNA_VERLOPEN: 'Bijna verlopen',
  VERLOPEN: 'Verlopen',
  ONTBREEKT: 'Ontbreekt',
}

// ─── Certificaat-typestatus (batch-6 cert) ────────────────────────────────────
//
// De afgeleide status per (medewerker × certificaattype) uit de resolver
// `bepaalTypeStatus` in @streep/utils — overal afgeleid, nergens opgeslagen.
// Vervangt het oude 4-status `CertificateStatus`-model op de plekken die de
// opvolgflow kennen (dashboard, screening, portal).

export const CERTIFICAAT_TYPE_STATUSSEN = [
  'ACTIEF',
  'VERLOOPT_BINNENKORT',
  'GEPLAND',
  'IN_OPLEIDING',
  'WACHT_OP_CERTIFICAAT',
  // batch-54 certificaten (FO-408 §A4) — de medewerker leverde zelf een
  // certificaat aan; HR moet het nog controleren. Bewust een EIGEN status en
  // geen sub-vlag: hij is nooit geldig (screening blijft betrouwbaar) maar
  // "ontbreekt" is onwaar en demotiverend als je net geüpload hebt.
  'WACHT_OP_CONTROLE',
  'VERLOPEN',
  'NIET_VAN_TOEPASSING',
  'ONTBREEKT',
] as const

export type CertificaatTypeStatus = (typeof CERTIFICAAT_TYPE_STATUSSEN)[number]

export const CERTIFICAAT_TYPE_STATUS_LABELS: Record<CertificaatTypeStatus, string> = {
  ACTIEF: 'Geldig',
  VERLOOPT_BINNENKORT: 'Verloopt binnenkort',
  GEPLAND: 'Herhaling gepland',
  IN_OPLEIDING: 'In opleiding',
  WACHT_OP_CERTIFICAAT: 'Wacht op certificaat',
  WACHT_OP_CONTROLE: 'Wacht op controle HR',
  VERLOPEN: 'Verlopen',
  NIET_VAN_TOEPASSING: 'Niet van toepassing',
  ONTBREEKT: 'Ontbreekt',
}

export const CERTIFICAAT_TYPE_STATUS_BADGE: Record<
  CertificaatTypeStatus,
  'success' | 'warning' | 'danger' | 'secondary' | 'info'
> = {
  ACTIEF: 'success',
  VERLOOPT_BINNENKORT: 'warning',
  GEPLAND: 'info',
  IN_OPLEIDING: 'info',
  WACHT_OP_CERTIFICAAT: 'warning',
  WACHT_OP_CONTROLE: 'info',
  VERLOPEN: 'danger',
  NIET_VAN_TOEPASSING: 'secondary',
  ONTBREEKT: 'danger',
}

// ─── Certificaat-verificatiestatus (batch-54 certificaten, FO-408) ────────────
//
// Opgeslagen op `EmployeeCertificate.verificatieStatus`. Los van `lifecycle`:
// die zegt iets over de plek in de versie-keten, dit over de controle van het
// bewijsstuk zelf. Alleen BEVESTIGD telt mee als geldig certificaat - de
// conditie zit centraal in `bepaalTypeStatus` (@streep/utils), niet per callsite.

export const CERTIFICAAT_VERIFICATIE_STATUSSEN = [
  'CONCEPT',
  'BEVESTIGD',
  'AFGEWEZEN',
] as const

export type CertificaatVerificatieStatus = (typeof CERTIFICAAT_VERIFICATIE_STATUSSEN)[number]

export const CERTIFICAAT_VERIFICATIE_STATUS_LABELS: Record<
  CertificaatVerificatieStatus,
  string
> = {
  CONCEPT: 'Wacht op controle HR',
  BEVESTIGD: 'Bevestigd',
  AFGEWEZEN: 'Afgewezen',
}

// ─── Portal-zelfaanlevering (batch-54 certificaten, FO-408 §A3) ───────────────
//
// GEDEELD schema: de API parset er de multipart-velden mee (`POST
// /me/certificates`) en het portal-formulier valideert er client-side mee vóór
// verzenden. Eén bron, dus het formulier kan niets sturen dat de API weigert
// (CLAUDE.md "Validatie & foutmeldingen").
//
// Datums komen als kale NL-kalenderdag (`yyyy-mm-dd`) binnen, niet als
// ISO-instant: de monteur kiest een dag, geen tijdstip. De API zet ze om naar
// een UTC-instant op de Amsterdam-dagstart.

const YMD = /^\d{4}-\d{2}-\d{2}$/

export const MeCertificaatConceptSchema = z
  .object({
    /** Gekozen certificaattype. Leeg = de "anders"-optie, dan is `vrijeTypeNaam` verplicht. */
    certificateTypeId: z.string().min(1).optional(),
    /** Vrije omschrijving bij de "anders"-optie; HR normaliseert het type bij verificatie. */
    vrijeTypeNaam: z.string().min(2, 'Omschrijf het certificaat').max(120).optional(),
    /** Uitgiftedatum (NL-kalenderdag). Default: vandaag, ingevuld door de client. */
    issuedAt: z.string().regex(YMD, 'Ongeldige datum (verwacht jjjj-mm-dd)'),
    /** Verloopdatum, optioneel - de monteur weet 'm niet altijd. */
    expiresAt: z.string().regex(YMD, 'Ongeldige datum (verwacht jjjj-mm-dd)').nullable().optional(),
    /** De afgeronde deelname waar de upload bij hoort (context voor HR). */
    opleidingDeelnameId: z.string().min(1).nullable().optional(),
  })
  .refine((v) => Boolean(v.certificateTypeId) || Boolean(v.vrijeTypeNaam), {
    message: 'Kies een certificaattype of omschrijf het zelf',
    path: ['certificateTypeId'],
  })
  .refine(
    (v) => !v.expiresAt || !v.issuedAt || v.expiresAt >= v.issuedAt,
    { message: 'Verloopdatum ligt vóór de uitgiftedatum', path: ['expiresAt'] },
  )

export type MeCertificaatConceptInput = z.infer<typeof MeCertificaatConceptSchema>

/** Code van het vangnet-certificaattype achter de "anders"-optie (HR normaliseert). */
export const CERTIFICAAT_TYPE_OVERIG_CODE = 'OVERIG-PORTAL'

/**
 * batch-71 verplichte-certificaten (FO-541 §D9) - de omschrijving bij "weet ik niet" in de
 * typekiezer. Hetzelfde vangnet-type, maar geen naam om als synoniem te onthouden.
 */
export const CERTIFICAAT_ONBEKEND_OMSCHRIJVING = 'Type onbekend, medewerker weet het niet'

/** HR-beoordeling van een aangeleverd concept (backoffice). */
export const CertificaatVerificatieSchema = z
  .object({
    besluit: z.enum(['BEVESTIGEN', 'AFWIJZEN']),
    /** Verplicht bij AFWIJZEN - de medewerker moet weten wat er mis was. */
    reden: z.string().max(500).nullable().optional(),
    /** Correcties die HR bij het bevestigen doorvoert (allemaal optioneel). */
    certificateTypeId: z.string().min(1).optional(),
    issuedAt: z.string().regex(YMD).optional(),
    expiresAt: z.string().regex(YMD).nullable().optional(),
  })
  .refine((v) => v.besluit !== 'AFWIJZEN' || Boolean(v.reden && v.reden.trim().length >= 3), {
    message: 'Geef een reden op zodat de medewerker weet wat er mis is',
    path: ['reden'],
  })

export type CertificaatVerificatieInput = z.infer<typeof CertificaatVerificatieSchema>

/** Certificaat-lifecycle (expliciet opgeslagen toestanden; string-union van de Prisma-enum). */
export type CertificaatLifecycle =
  | 'ACTIEF'
  | 'VERVANGEN'
  | 'VERVALLEN'
  | 'NIET_VAN_TOEPASSING'
  | 'GEANNULEERD'

export const CERTIFICAAT_LIFECYCLE_LABELS: Record<CertificaatLifecycle, string> = {
  ACTIEF: 'Actief',
  VERVANGEN: 'Vervangen',
  VERVALLEN: 'Vervallen',
  NIET_VAN_TOEPASSING: 'Niet van toepassing',
  GEANNULEERD: 'Geannuleerd',
}

export const CERTIFICAAT_NIET_NODIG_REDENEN = [
  'ANDERE_FUNCTIE',
  'NIET_MEER_BIJ_KLANT',
  'VERVANGEN_DOOR_ANDER_TYPE',
  'UIT_DIENST',
  'OVERIG',
] as const

export type CertificaatNietNodigReden = (typeof CERTIFICAAT_NIET_NODIG_REDENEN)[number]

export const CERTIFICAAT_NIET_NODIG_REDEN_LABELS: Record<CertificaatNietNodigReden, string> = {
  ANDERE_FUNCTIE: 'Andere functie',
  NIET_MEER_BIJ_KLANT: 'Niet meer bij deze klant',
  VERVANGEN_DOOR_ANDER_TYPE: 'Vervangen door ander type',
  UIT_DIENST: 'Uit dienst',
  OVERIG: 'Overig',
}

/** Eén rij in de HR-werkvoorraad op /hrm/certificaten (per medewerker × type). */
export interface CertificaatWerkvoorraadRijDTO {
  employeeId: string
  employeeNumber: string | null
  firstName: string
  lastName: string
  certificateTypeId: string
  certificateTypeCode: string
  certificateTypeName: string
  status: CertificaatTypeStatus
  /** Vervaldatum van de leidende versie (ISO), null = verloopt nooit/geen versie. */
  geldigTot: string | null
  /** NL-kalenderdagen tot verloop (alleen bij ACTIEF/VERLOOPT_BINNENKORT). */
  dagenTotVerloop: number | null
  /** Id van de leidende versie (voor acties: upload/correctie/niet-nodig). */
  leidendeVersieId: string | null
  versie: number | null
  lifecycle: CertificaatLifecycle | null
  /** Geplande/lopende herhaling (uit de relevante deelname). */
  geplandeOpleiding: {
    deelnameId: string
    sessieId: string | null
    opleidingId: string
    opleidingNaam: string
    datum: string | null
    eindDatum: string | null
  } | null
  /** Verantwoordelijke voor de opvolging = planner van de sessie (createdBy). */
  verantwoordelijke: { userId: string; naam: string | null } | null
  /** Tot wanneer dit opvolg-item gesnoozed is (null = niet gesnoozed). */
  snoozeTot: string | null
  /** Verlopen + geen actie langer dan de instelbare drempel (en niet gesnoozed). */
  nietOpgepakt: boolean
  /** Status komt uit een vereiste (klant/werkbon/project) i.p.v. een eigen versie. */
  isVereist: boolean
  /**
   * batch-54 certificaten (FO-408 §A4) — er ligt een door de medewerker
   * aangeleverd CONCEPT op HR-controle te wachten.
   *
   * Bewust NAAST de afgeleide status en niet erin: een vernieuwing die vóór de
   * vervaldatum wordt geüpload laat de status op ACTIEF/VERLOOPT_BINNENKORT
   * staan (het oude certificaat is immers nog geldig). Filteren op
   * `status === 'WACHT_OP_CONTROLE'` zou die - het meest voorkomende geval -
   * missen en HR op een lege te-controleren-lijst zetten.
   */
  heeftOpenConcept: boolean
}

export interface CertificaatWerkvoorraadKpisDTO {
  verlopen: number
  verlooptBinnen30: number
  verlooptBinnen60: number
  gepland: number
  wachtOpCertificaat: number
  /** batch-54 certificaten (FO-408) — zelf aangeleverd, wacht op HR-controle. */
  wachtOpControle: number
  nietOpgepakt: number
  zonderVerplichtCertificaat: number
}

export interface CertificaatWerkvoorraadResponseDTO {
  data: CertificaatWerkvoorraadRijDTO[]
  meta: {
    total: number
    kpis: CertificaatWerkvoorraadKpisDTO
    certificateTypes: CertificateTypeOptionDTO[]
    instellingen: {
      waarschuwingstermijnenDagen: number[]
      nietOpgepaktDrempelDagen: number
      snoozeDagen: number
    }
  }
}

/**
 * Batch-6 cert §5b — één finding uit de plan-screening (409
 * CERTIFICAAT_VEREISTE `details.findings`). Gedeeld tussen de API
 * (certificaat-screening.ts) en de plan-UI's (cert-vereiste-dialoog).
 */
export interface CertScreeningFindingDTO {
  employeeId: string
  employeeNaam: string
  certificateTypeId: string
  certificateTypeNaam: string
  status: CertificaatTypeStatus
  ernst: 'BLOKKEER' | 'WAARSCHUWING' | 'HINT'
  geplandeDatum: string | null
  bericht: string
}

export const CERTIFICATE_STATUS_BADGE: Record<
  CertificateStatus,
  'success' | 'warning' | 'destructive' | 'secondary'
> = {
  ACTIEF: 'success',
  BIJNA_VERLOPEN: 'warning',
  VERLOPEN: 'destructive',
  ONTBREEKT: 'secondary',
}

export interface CertificateOverviewCellDTO {
  certificateTypeId: string
  certificateTypeCode: string
  certificateTypeName: string
  status: CertificateStatus
  issuedAt: string | null
  expiresAt: string | null
}

export interface CertificateOverviewRowDTO {
  employeeId: string
  employeeNumber: string | null
  firstName: string
  lastName: string
  certificates: CertificateOverviewCellDTO[]
}

export interface CertificateTypeOptionDTO {
  id: string
  code: string
  name: string
}

export interface CertificateOverviewResponseDTO {
  data: CertificateOverviewRowDTO[]
  meta: {
    total: number
    certificateTypes: CertificateTypeOptionDTO[]
  }
}

// ─── Eigen skills + certificaten (portal /mijn-skills) ───────────────────────

export interface EigenSkillDTO {
  skillId: string
  name: string
  category: SkillCategory
  // Skillmatrix niveauteksten (batch-8) — per-skill schaal voor de slider.
  schaalMin: number
  schaalMax: number
  // PR-F (spec 16 §9) — eigen-beoordeling + level + niveaubeschrijvingen
  level: number | null
  eigenBeoordeling: number | null
  managementBeoordeling: number | null
  onderbouwing: string | null
  levelDescriptions: SkillLevelDescriptionDTO[]
}

export interface EigenCertificaatDTO {
  id: string
  certificateType: {
    id: string
    name: string
    code: string
  }
  issuedAt: string
  expiresAt: string | null
  notes: string | null
  // Batch-6 cert — versie-keten + document-velden (optioneel: oudere callers
  // kennen ze niet).
  versie?: number
  lifecycle?: CertificaatLifecycle
  geldigVanaf?: string | null
  certificaatNummer?: string | null
  uitgever?: string | null
  // batch-54 certificaten (FO-408) — verificatietoestand van deze versie.
  // Optioneel: oudere callers kennen het veld niet en behandelen het als
  // BEVESTIGD (de DB-default).
  verificatieStatus?: CertificaatVerificatieStatus
  verificatieReden?: string | null
  attachments: Array<{
    id: string
    fileName: string
    fileSize: number
    mimeType: string
    createdAt: string
  }>
}

/**
 * Batch-6 cert §4 — portal "Mijn certificaten": per certificaattype de
 * afgeleide status (resolver, zelfde waarheid als backoffice), de geplande
 * herhaling en de versie-historie (read-only). GET /me/certificaten-overzicht.
 */
export interface MijnCertificaatTypeDTO {
  certificateTypeId: string
  certificateTypeNaam: string
  certificateTypeCode: string
  status: CertificaatTypeStatus
  geldigTot: string | null
  dagenTotVerloop: number | null
  geplandeHerhaling: {
    opleidingNaam: string
    datum: string | null
    sessieId: string | null
    /** batch-63 (FO-468) - aangevraagd bij de opleider, nog niet bevestigd. */
    onderVoorbehoud: boolean
  } | null
  actueleVersie: EigenCertificaatDTO | null
  historie: EigenCertificaatDTO[]
}

/**
 * Bereken status uit `expiresAt`. Drempel `bijnaVerlopenDagen` is 60 dagen
 * (productblauwdruk §5.17 — strikter dan API-default 90d, voor portal-
 * waarschuwingen op de monteur-app).
 */
export function berekenCertificaatStatus(
  expiresAt: string | null,
  bijnaVerlopenDagen = 60,
): Exclude<CertificateStatus, 'ONTBREEKT'> {
  if (!expiresAt) return 'ACTIEF'
  const now = Date.now()
  const exp = new Date(expiresAt).getTime()
  const drempel = bijnaVerlopenDagen * 24 * 60 * 60 * 1000
  if (exp < now) return 'VERLOPEN'
  if (exp - now < drempel) return 'BIJNA_VERLOPEN'
  return 'ACTIEF'
}

// Cursussen (HRM F1.5) zijn verwijderd in de opleidingen-consolidatie
// (slice 6b). Het opleidingen-domein (zie `./opleiding`) verving Course /
// EmployeeCourse volledig — DTO's, labels en enums hieronder bestaan niet meer.

// ─── PR-F: skill-uitbreiding (spec 16) ───────────────────────────────────────

export type Werktype = 'onderhoud' | 'service' | 'projecten'

export const WERKTYPE_LABELS: Record<Werktype, string> = {
  onderhoud: 'Onderhoud',
  service:   'Service',
  projecten: 'Projecten',
}

export interface SkillLevelDescriptionDTO {
  level: number // niveau binnen de schaal van de skill
  description: string
  // Skillmatrix niveauteksten (batch-8) — extra competentie-bullets onder de hoofdtekst.
  competenties: string[]
}

// ─── Skillmatrix niveauteksten + instelbare schaal (batch-8 skills) ──────────

/** Harde bovengrens voor de schaal (slider + API + import). */
export const SKILL_SCHAAL_MAX_ABS = 10

/** Eén niveau-rij in de PUT-set. `competenties` optioneel (default []). */
export const SkillLevelDescriptionInputSchema = z.object({
  level: z.number().int().min(0).max(SKILL_SCHAAL_MAX_ABS),
  description: z.string().trim().min(1, 'Tekst is verplicht').max(2000, 'Maximaal 2000 tekens'),
  competenties: z
    .array(z.string().trim().min(1, 'Lege competentie').max(500, 'Maximaal 500 tekens'))
    .max(20, 'Maximaal 20 competenties')
    .optional()
    .default([]),
})
export type SkillLevelDescriptionInput = z.infer<typeof SkillLevelDescriptionInputSchema>

/**
 * Hele set niveauteksten per skill (PUT /skills/:id/levels). De range t.o.v. de
 * skill-schaal (schaalMin..schaalMax) wordt server-side gecheckt — dynamisch, dus
 * niet in dit (statische) schema.
 */
export const SkillLevelDescriptionsPutSchema = z
  .array(SkillLevelDescriptionInputSchema)
  .max(SKILL_SCHAAL_MAX_ABS + 1, 'Te veel niveaus')
export type SkillLevelDescriptionsPut = z.infer<typeof SkillLevelDescriptionsPutSchema>

/** Schaal-instelling per skill (PATCH /skills/:id); min < max, max <= 10. */
export const SkillScalePatchSchema = z
  .object({
    schaalMin: z.number().int().min(0).max(SKILL_SCHAAL_MAX_ABS),
    schaalMax: z.number().int().min(1).max(SKILL_SCHAAL_MAX_ABS),
  })
  .refine((v) => v.schaalMin < v.schaalMax, {
    message: 'Minimum moet kleiner zijn dan maximum',
    path: ['schaalMax'],
  })
export type SkillScalePatch = z.infer<typeof SkillScalePatchSchema>

export interface SkillCategoryConfigDTO {
  id: string
  code: string        // referentie naar SkillCategory-enum
  name: string
  position: number
  factor: number      // 1-10
  minLevelOnderhoud: number | null
  minLevelService: number | null
  minLevelProjecten: number | null
}

export interface SkillKostenConfigDTO {
  id: string
  minimumLoon: number
  maximumLoon: number
  minimumUurtarief: number | null
  maximumUurtarief: number | null
}

export interface SkillKostenCategorieScoreDTO {
  code: string
  gemiddeld: number
  gewogenBijdrage: number
}

export interface SkillKostenResultDTO {
  gewogenGemiddeld: number
  indicatiefSalaris: number
  indicatiefUurtarief: number
  categorieScores: SkillKostenCategorieScoreDTO[]
}

export interface InzetbaarheidsCategorieDTO {
  code: string
  gemiddeld: number
  geschiktOnderhoud: boolean
  geschiktService: boolean
  geschiktProjecten: boolean
  /** Override-waardes (null = geen override → toon berekening). */
  overrideOnderhoud: boolean | null
  overrideService: boolean | null
  overrideProjecten: boolean | null
}

export interface InzetbaarheidsResponseDTO {
  perCategorie: InzetbaarheidsCategorieDTO[]
  totaal: {
    geschiktOnderhoud: boolean
    geschiktService: boolean
    geschiktProjecten: boolean
  }
  kosten: SkillKostenResultDTO
}

/** Per-skill dual-assessment-record (backoffice skill-matrix detail). */
export interface DualAssessmentDTO {
  skillId: string
  level: number | null
  eigenBeoordeling: number | null
  managementBeoordeling: number | null
  onderbouwing: string | null
  beoordeeldOp: string | null
}

// ─── Matchfunctie (HRM F1.5) ─────────────────────────────────────────────────

/**
 * Eis voor matching op skill + minimaal niveau.
 *   `minLevel = null` betekent "skill aanwezig is genoeg, niveau wordt genegeerd".
 */
export interface MatchRequirementDTO {
  skillId: string
  minLevel: SkillLevel | null
}

export interface MatchResultRowDTO {
  employeeId: string
  fullName: string
  score: number // 0-100
  matchedSkills: Array<{ id: string; name: string; category: SkillCategory; level: SkillLevel | null }>
  missingSkills: Array<{ id: string; name: string; category: SkillCategory; reason: 'NOT_PRESENT' | 'BELOW_LEVEL' }>
  experienceLevel: ExperienceLevel | null
  availableOnDate: boolean
}

// ─── Skillmatrix v2 — vaardigheidsboom, klantprofielen, matchmaker ────────────

/**
 * Profiel-enums als string-unions (parallelt Prisma-enums in packages/db).
 */
export type ProfielRichting =
  | 'ONDERHOUD'
  | 'ELEKTRO'
  | 'ALLROUND'
  | 'STORINGSDIENST'
  | 'SOFTWARE_AUTOMATISERING'
  | 'PROJECTEN'
  | 'SPECIALISTISCH'

export type ProfielNiveau = 'JUNIOR' | 'MEDIOR' | 'SENIOR' | 'SPECIALIST'

export type ProfielStatus = 'CONCEPT' | 'ACTIEF' | 'INACTIEF'

/**
 * DienstType string-union voor DTO's (parallelt Prisma DienstType-enum).
 * Gebruik dit type in DTOs i.p.v. directe `@prisma/client` import.
 */
export type DienstTypeKey =
  | 'DAGDIENST'
  | 'PLOEGEN_2'
  | 'PLOEGEN_3'
  | 'PLOEGEN_4'
  | 'PLOEGEN_5'
  | 'CONSIGNATIE'

/** Vaardigheidsboom-groep (hoofdgroep of subgroep). */
export interface SkillGroupDTO {
  id: string
  name: string
  parentId: string | null
  position: number
  category: SkillCategory | null
  factor: number
  children: SkillGroupDTO[]
  skills: SkillDTO[]
}

/** Borgingsregel — vaardigheid X vereist vaardigheid Y op minimumScore. */
export interface BorgingsregelDTO {
  id: string
  skillId: string
  skillNaam: string
  vereistSkillId: string
  vereistSkillNaam: string
  minimumScore: number
  activerenBoven: number
}

/** Borgingswaarschuwing — gegenereerd als borging geschonden is. */
export interface BorgingsWaarschuwingDTO {
  skill: string
  skillScore: number
  vereistSkill: string
  vereistScore: number
  minimumVereist: number
  melding: string
}

/** Klant-skillprofiel op een Client-relatie. */
export interface RelationSkillProfileDTO {
  id: string
  clientId: string
  name: string
  richting: ProfielRichting
  niveau: ProfielNiveau
  specialisatie: string | null
  status: ProfielStatus
  dienstType: DienstTypeKey | null
  groupScores: Array<{
    groupId: string
    groupName: string
    score: number
  }>
  highlights: Array<{
    groupId?: string | null
    skillId?: string | null
  }>
  totaalScore: number
}

/** Herbruikbaar standaardprofiel (klant of medewerker). */
export interface SkillProfilePresetDTO {
  id: string
  name: string
  richting: ProfielRichting
  niveau: ProfielNiveau
  dienstType: DienstTypeKey | null
  groupScores: Record<string, number>
  highlights: {
    groups: string[]
    skills: string[]
  }
  isKlant: boolean
  position: number
}

/** Deelbare assessment-link voor een medewerker. */
export interface SkillAssessmentShareDTO {
  id: string
  employeeId: string
  token: string
  invullerNaam: string | null
  invullerEmail: string | null
  verlooptOp: string
  ingevuld: boolean
  ingevuldOp: string | null
}

/**
 * Matchmaker v2 — criteria voor matching medewerker ↔ klant.
 * Gewichten: vaardigheidsmatch 60%, highlights 15%, profiel 10%,
 * rooster 5%, afstand 5%, historie 5%.
 */
export interface MatchCriteriaDTO {
  /** Gewenste score per hoofdgroep-id (1-10). */
  groupScores: Record<string, number>
  /** Factor (gewicht) per hoofdgroep-id. */
  groupFactors: Record<string, number>
  /** Highlights: aanvullende eisen op groep of skill-niveau. */
  highlights: Array<{ groupId?: string | null; skillId?: string | null; minScore?: number }>
  richting: ProfielRichting
  niveau: ProfielNiveau
  /** Maximale afstand in km (null = onbeperkt). */
  maxAfstandKm?: number | null
  dienstType?: DienstTypeKey | null
}

/** Per-groep breakdown in een matchresultaat. */
export interface MatchGroupBreakdownDTO {
  groupId: string
  groupName: string
  vereist: number
  werkelijk: number
  bijdrage: number
}

/** Volledig matchresultaat voor één medewerker-profiel-combinatie. */
export interface MatchResultDTO {
  employeeId: string
  employeeName: string
  totalMatchPercentage: number
  groupMatchDetails: MatchGroupBreakdownDTO[]
  highlightScore: number
  profielMatch: {
    richtingMatch: boolean
    niveauMatch: boolean
    score: number
  }
  roosterMatch: boolean | null
  afstandKm: number | null
  eerderGeweest: boolean
  aantalKeerGeweest: number
  borgingsWaarschuwingen: string[]
}

/**
 * Pure helper voor de matchfunctie — deterministisch, zonder DB-toegang.
 *
 *   score = (sum_per_req(min(empLevel, minLevel) / minLevel)) / N * 100
 *
 * Een skill zonder ingesteld niveau (`level=null`) telt als level 1 als de
 * vereiste een minLevel heeft (we weten alleen "aanwezig"). Als er geen
 * minLevel is, telt aanwezigheid voor 100% van die regel.
 *
 * Lege requirements → score 0 (caller filtert dan zelf).
 */
export function berekenMatchPercentage(
  employeeSkills: Array<{ skillId: string; level: SkillLevel | null }>,
  requirements: MatchRequirementDTO[],
): number {
  if (requirements.length === 0) return 0
  const skillMap = new Map(employeeSkills.map((s) => [s.skillId, s.level]))

  let total = 0
  for (const req of requirements) {
    const empLevel = skillMap.get(req.skillId)
    if (empLevel === undefined) continue // skill ontbreekt → 0 voor deze regel
    if (req.minLevel === null) {
      total += 1
      continue
    }
    const effectiveLevel = empLevel ?? 1 // null-niveau telt als 1
    if (effectiveLevel >= req.minLevel) {
      total += 1
    } else {
      total += effectiveLevel / req.minLevel
    }
  }
  return Math.round((total / requirements.length) * 100)
}
